/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import {CopyIcon} from '@ion/dls/src/icons/CopyIcon';
import {RedoIcon} from '@ion/dls/src/icons/RedoIcon';
import {UndoIcon} from '@ion/dls/src/icons/UndoIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export interface EditableRowsGridToolbarProps {
  toolbarLeftContent?: React.ReactNode;
  undoLabel: string;
  redoLabel: string;
  cloneLabel: string;
  addRowLabel: string;
  undoDisabled: boolean;
  redoDisabled: boolean;
  cloneDisabled: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClone: () => void;
  onAddRow: () => void;
}

const useStyles = makeStyles(() => ({
  actions : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    marginBottom   : '8px'
  },
  toolbarLeft : {
    ...dlsAlt('typography', 'label2'),
    color : dlsColor('grey2')
  },
  actionsRight : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    gap            : '10px'
  }
}));

/**
 * Renders the editable-grid toolbar actions and optional left-side contextual content.
 *
 * ```typescript
 * <EditableRowsGridToolbar
 *   toolbarLeftContent={toolbarLeftContent}
 *   undoLabel={undoLabel}
 *   redoLabel={redoLabel}
 *   cloneLabel={cloneLabel}
 *   addRowLabel={addRowLabel}
 *   undoDisabled={undoDisabled}
 *   redoDisabled={redoDisabled}
 *   cloneDisabled={cloneDisabled}
 *   onUndo={onUndo}
 *   onRedo={onRedo}
 *   onClone={onClone}
 *   onAddRow={onAddRow}
 * />
 * ```
 *
 * @param   toolbarLeftContent Optional content rendered on the left side of the toolbar.
 * @param   undoLabel          Undo action label.
 * @param   redoLabel          Redo action label.
 * @param   cloneLabel         Clone row action label.
 * @param   addRowLabel        Add row action label.
 * @param   undoDisabled       Disables the undo action when true.
 * @param   redoDisabled       Disables the redo action when true.
 * @param   cloneDisabled      Disables the clone action when true.
 * @param   onUndo             Invoked when undo is clicked.
 * @param   onRedo             Invoked when redo is clicked.
 * @param   onClone            Invoked when clone is clicked.
 * @param   onAddRow           Invoked when add row is clicked.
 * @returns The editable rows grid toolbar component.
 */
const EditableRowsGridToolbar = ({
  undoLabel,
  redoLabel,
  cloneLabel,
  addRowLabel,
  undoDisabled,
  redoDisabled,
  cloneDisabled,
  onUndo,
  onRedo,
  onClone,
  onAddRow,
  toolbarLeftContent = null
}: EditableRowsGridToolbarProps) => {
  const classes = useStyles();

  return (
    <div className={classes.actions}>
      <div className={classes.toolbarLeft}>
        {toolbarLeftContent}
      </div>
      <div className={classes.actionsRight}>
        <TertiaryButton onClick={onUndo}
          disabled={undoDisabled}
          startIcon={<UndoIcon size={16} />}>
          {undoLabel}
        </TertiaryButton>
        <TertiaryButton onClick={onRedo}
          disabled={redoDisabled}
          startIcon={<RedoIcon size={16} />}>
          {redoLabel}
        </TertiaryButton>
        <TertiaryButton onClick={onClone}
          disabled={cloneDisabled}
          startIcon={<CopyIcon />}>
          {cloneLabel}
        </TertiaryButton>
        <TertiaryButton onClick={onAddRow}
          startIcon={<Add />}>
          {addRowLabel}
        </TertiaryButton>
      </div>
    </div>
  );
};

export default EditableRowsGridToolbar;
